import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SelectTable, Table } from "@models/table";
import { CustomTableItem } from "@models/custom-table-item";
import { ManufacturerModel } from "@models/manufacturer-model";
import { groupBy } from "@shared/lib/utils";

export interface InitialState {
  systemTable: Table[] | undefined;
  manufacturersTable: Table[] | undefined;
  warehousesTable: Table[] | undefined;
  tagsTable: Table[] | undefined;
  sortCodesTable: Table[] | undefined;
  itemGroupsTable: Table[] | undefined;
  banksTable: Table[] | undefined;
  modelsTable: ManufacturerModel[] | undefined;
  modelsObjectTable: Record<number, ManufacturerModel[]>;
  customTables: Table[] | undefined;
  customTablesItems: CustomTableItem[] | undefined;
  incomesTable: SelectTable[] | undefined;
  outcomesTable: SelectTable[] | undefined;
  kupaTable: SelectTable[] | undefined;
  banksAccountTable: SelectTable[] | undefined;
}

const initialState: InitialState = {
  manufacturersTable: undefined,
  warehousesTable: undefined,
  tagsTable: undefined,
  sortCodesTable: undefined,
  itemGroupsTable: undefined,
  banksTable: undefined,
  systemTable: undefined,
  modelsTable: undefined,
  modelsObjectTable: {},
  customTables: undefined,
  customTablesItems: undefined,
  incomesTable: undefined,
  outcomesTable: undefined,
  kupaTable: undefined,
  banksAccountTable: undefined,
};

export const tables = createSlice({
  name: "tables",
  initialState,
  reducers: {
    setManufacturersTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.manufacturersTable = action.payload;
    },
    setWarehousesTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.warehousesTable = action.payload;
    },
    setTagsTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.tagsTable = action.payload;
    },
    setSortCodesTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.sortCodesTable = action.payload;
    },
    setItemGroupsTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.itemGroupsTable = action.payload;
    },
    setBanksTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.banksTable = action.payload;
    },
    setSystemTable: (state, action: PayloadAction<Table[] | undefined>) => {
      state.systemTable = action.payload;
    },
    setModelsTable: (state, action: PayloadAction<ManufacturerModel[] | undefined>) => {
      state.modelsTable = action.payload;
      const groupsObject = groupBy(action.payload, "manufacturer_id");
      state.modelsObjectTable = groupsObject;
    },
    setModelsForManufacturer: (
      state,
      action: PayloadAction<{ manufacturerId: number; models: ManufacturerModel[] }>,
    ) => {
      const { manufacturerId, models } = action.payload;
      state.modelsObjectTable[manufacturerId] = models;
    },
    setCustomTables: (state, action: PayloadAction<Table[] | undefined>) => {
      state.customTables = action.payload;
    },
    setCustomTablesItems: (state, action: PayloadAction<CustomTableItem[] | undefined>) => {
      state.customTablesItems = action.payload;
    },
    setIncomesTable: (state, action: PayloadAction<SelectTable[] | undefined>) => {
      state.incomesTable = action.payload;
    },
    setOutcomesTable: (state, action: PayloadAction<SelectTable[] | undefined>) => {
      state.outcomesTable = action.payload;
    },
    setKupaTable: (state, action: PayloadAction<SelectTable[] | undefined>) => {
      state.kupaTable = action.payload;
    },
    setBanksAccountTable: (state, action: PayloadAction<SelectTable[] | undefined>) => {
      state.banksAccountTable = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase("logout", () => initialState);
  },
});

export const {
  setManufacturersTable,
  setWarehousesTable,
  setTagsTable,
  setSortCodesTable,
  setItemGroupsTable,
  setBanksTable,
  setSystemTable,
  setModelsTable,
  setModelsForManufacturer,
  setCustomTables,
  setCustomTablesItems,
  setIncomesTable,
  setOutcomesTable,
  setKupaTable,
  setBanksAccountTable,
} = tables.actions;

export default tables.reducer;
